/**
 * One block on the canvas, drawn the way the customer will see it and edited
 * where it sits.
 *
 * A heading is always an input styled as the heading. The words are the
 * customer's paragraphs and bullets until the block is picked, and then the
 * same words in a textarea set in the same type — so the page reads as the
 * document and still takes typing anywhere. Decision 81.
 */
import { isProposalImage, MAX_SECTION_FILES } from '@fieldops/shared'
import {
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '../../lib/cn.js'
import { fileUrl, uploadFile } from '../../lib/files.js'
import { Icon, type IconName } from '../Icon.js'
import { AssistMenu, SuggestionCard, type AssistAction, type Suggestion } from './AssistMenu.js'
import { SECTION_LABEL, type DraftFile, type DraftSection } from './draft.js'
import { EditablePricing, type LineEditing } from './CanvasLines.js'
import { CoverSheet } from './Cover.js'
import type { LetterheadBusiness } from './Letterhead.js'
import { coverDetails, FileBadge, Prose, type Choice, type ProposalView } from './ProposalPage.js'

export const BLOCK_ICON: Record<DraftSection['kind'], IconName> = {
  COVER: 'cover',
  TEXT: 'text',
  IMAGE: 'image',
  PRICING: 'pricing',
  TERMS: 'terms',
}

export interface BlockControls {
  canMoveUp: boolean
  canMoveDown: boolean
  onMove: (by: -1 | 1) => void
  onDuplicate: (() => void) | null
  onRemove: () => void
  /** Drag handle props from the sortable wrapper, when the block can be dragged. */
  handle: HTMLAttributes<HTMLButtonElement> | null
}

export function CanvasBlock({
  section,
  selected,
  onSelect,
  onPatch,
  controls,
  view,
  business,
  number,
  date,
  defaultTerms,
  mode,
  jobId,
  assist,
  onOptionFocus,
  lineEditing,
}: {
  section: DraftSection
  selected: boolean
  onSelect: () => void
  /** `group` coalesces typing into one undo step. */
  onPatch: (next: Partial<DraftSection>, group?: string) => void
  controls: BlockControls
  /** The whole proposal as the customer's page reads it, for the price. */
  view: ProposalView
  /** The paper the cover is printed on. */
  business: LetterheadBusiness
  number: string
  /** The letter date, as the cover prints it. */
  date: string | null
  defaultTerms: string
  mode: 'quote' | 'template'
  jobId: string | null
  assist: {
    available: boolean
    suggestion: Suggestion | null
    onAsk: (action: AssistAction, instruction: string | null, selection: { start: number; end: number } | null) => void
    onReplace: () => void
    onInsert: () => void
    onRetry: () => void
    onDiscard: () => void
  }
  onOptionFocus: (optionKey: string) => void
  /** The price's line items and extras, edited where they print. */
  lineEditing: LineEditing
}) {
  const [menu, setMenu] = useState(false)
  const [range, setRange] = useState<{ start: number; end: number } | null>(null)
  const label = SECTION_LABEL[section.kind]
  const [choice, setChoice] = useState<Choice>({ optionId: null, addOnIds: [] })

  const body = (placeholder: string, className?: string) => (
    <BodyField
      section={section}
      editing={selected}
      placeholder={placeholder}
      className={className}
      onChange={(value) => onPatch({ body: value }, `body-${section.key}`)}
      onRange={setRange}
      label={`${label} words`}
    />
  )

  const coverPage = section.kind === 'COVER'

  return (
    <div
      data-block={section.key}
      onClick={onSelect}
      className={cn(
        'group/block relative',
        coverPage
          ? cn('rounded-t-md', selected && 'ring-2 ring-[var(--p-brand)] ring-inset')
          : cn(
              '-mx-4 rounded-xl px-4 transition-[box-shadow,background-color] sm:-mx-6 sm:px-6',
              selected
                ? 'bg-white shadow-[0_0_0_2px_var(--p-brand),0_12px_32px_-16px_rgb(0_0_0/0.25)]'
                : 'hover:shadow-[0_0_0_1px_var(--color-ink-200)]',
            ),
      )}
    >
      <BlockToolbar
        label={label}
        icon={BLOCK_ICON[section.kind]}
        selected={selected}
        controls={controls}
        aiAvailable={assist.available}
        aiLabel={range ? 'Rewrite the selection' : 'Writing help'}
        menuOpen={menu}
        onMenu={() => setMenu((open) => !open)}
        menu={
          menu ? (
            <AssistMenu
              hasText={section.body.trim() !== ''}
              hasSelection={range !== null}
              onClose={() => setMenu(false)}
              onPick={(action, instruction) => {
                setMenu(false)
                assist.onAsk(action, instruction, range)
              }}
            />
          ) : null
        }
      />

      {section.kind === 'COVER' ? (
        <CoverSheet
          business={business}
          number={number}
          date={date}
          className="rounded-t-md ring-0"
          photo={
            section.image || selected ? (
              <PhotoField section={section} onPatch={onPatch} jobId={jobId} mode={mode} variant="cover" active={selected} />
            ) : null
          }
          title={
            <HeadingInput
              label="Cover headline"
              value={section.title}
              placeholder="A headline for the cover"
              className="w-full text-3xl leading-tight font-extrabold text-[var(--p-brand-text)] sm:text-5xl"
              onChange={(value) => onPatch({ title: value }, `title-${section.key}`)}
            />
          }
          intro={body('A line or two to introduce the proposal', 'text-base text-ink-700 sm:text-lg')}
          details={coverDetails(view)}
        />
      ) : section.kind === 'IMAGE' ? (
        <section className="py-5">
          <HeadingInput
            label="Photos heading"
            value={section.title}
            placeholder="A heading (optional)"
            className="w-full text-base font-bold text-ink-900"
            onChange={(value) => onPatch({ title: value }, `title-${section.key}`)}
          />
          <FilesField section={section} onPatch={onPatch} jobId={jobId} mode={mode} className="mt-3" />
          {body('A note under the photos (optional)', 'mt-3 text-sm text-ink-600')}
        </section>
      ) : section.kind === 'PRICING' ? (
        <div className="py-5">
          <HeadingInput
            label="Pricing heading"
            value={section.title}
            placeholder={view.options.length > 0 ? 'Your options' : 'The work'}
            className="w-full border-b-2 border-[var(--p-brand)] pb-1.5 text-lg font-bold text-ink-900"
            onChange={(value) => onPatch({ title: value }, `title-${section.key}`)}
          />
          {body('Words above the price (optional)', 'mt-3')}
          <div
            className="pointer-events-auto"
            onClickCapture={(event) => {
              const card = (event.target as HTMLElement).closest('[role="radio"]')
              const key = card?.getAttribute('data-option')
              if (key) onOptionFocus(key)
            }}
          >
            <EditablePricing view={view} editing={lineEditing} choice={choice} onChoice={setChoice} />
          </div>
        </div>
      ) : (
        <section className="py-5">
          <HeadingInput
            label={`${label} heading`}
            value={section.title}
            placeholder={section.kind === 'TERMS' ? 'Terms' : 'A heading'}
            className="w-full border-b-2 border-[var(--p-brand)] pb-1.5 text-lg font-bold text-ink-900"
            onChange={(value) => onPatch({ title: value }, `title-${section.key}`)}
          />
          {section.kind === 'TERMS'
            ? body(defaultTerms ? `Blank prints the default terms:\n\n${defaultTerms}` : 'The terms', 'mt-3 text-xs text-ink-600')
            : body('Write here, or ask for help with the ✦ button', 'mt-3')}
        </section>
      )}

      {assist.suggestion ? (
        <div className="pb-4">
          <SuggestionCard
            suggestion={assist.suggestion}
            onReplace={assist.onReplace}
            onInsert={assist.onInsert}
            onRetry={assist.onRetry}
            onDiscard={assist.onDiscard}
          />
        </div>
      ) : null}
    </div>
  )
}

function BlockToolbar({
  label,
  icon,
  selected,
  controls,
  aiAvailable,
  aiLabel,
  menuOpen,
  onMenu,
  menu,
}: {
  label: string
  icon: IconName
  selected: boolean
  controls: BlockControls
  aiAvailable: boolean
  aiLabel: string
  menuOpen: boolean
  onMenu: () => void
  menu: ReactNode
}) {
  return (
    <>
      {controls.handle ? (
        <button
          type="button"
          aria-label={`Drag ${label.toLowerCase()}`}
          title="Drag to move"
          {...controls.handle}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'absolute top-5 -left-9 hidden size-7 cursor-grab touch-none place-items-center rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-700 active:cursor-grabbing sm:grid',
            selected ? 'opacity-100' : 'opacity-0 group-hover/block:opacity-100 focus-visible:opacity-100',
          )}
        >
          <Icon name="grip" className="size-4" />
        </button>
      ) : null}

      <div
        className={cn(
          'absolute -top-4 right-3 z-20 flex items-center gap-0.5 rounded-lg bg-white p-0.5 font-sans shadow-md ring-1 ring-ink-200 transition-opacity',
          selected || menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover/block:pointer-events-auto group-hover/block:opacity-100',
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <span className="flex items-center gap-1 px-1.5 text-[11px] font-semibold text-ink-500">
          <Icon name={icon} className="size-3.5" />
          {label}
        </span>
        {aiAvailable ? (
          <span className="relative">
            <ToolButton
              label={aiLabel}
              icon="sparkles"
              onClick={onMenu}
              active={menuOpen}
              className="text-violet-700 hover:bg-violet-50"
            />
            {menu}
          </span>
        ) : null}
        <ToolButton label="Move up" icon="back" rotate disabled={!controls.canMoveUp} onClick={() => controls.onMove(-1)} />
        <ToolButton label="Move down" icon="forward" rotate disabled={!controls.canMoveDown} onClick={() => controls.onMove(1)} />
        {controls.onDuplicate ? <ToolButton label="Duplicate" icon="copy" onClick={controls.onDuplicate} /> : null}
        <ToolButton label={`Remove ${label.toLowerCase()}`} icon="trash" onClick={controls.onRemove} />
      </div>
    </>
  )
}

function ToolButton({
  label,
  icon,
  onClick,
  disabled,
  rotate,
  active,
  className,
}: {
  label: string
  icon: IconName
  onClick: () => void
  disabled?: boolean
  rotate?: boolean
  active?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-expanded={active === undefined ? undefined : active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'grid size-7 place-items-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-30',
        active && 'bg-violet-100',
        className,
      )}
    >
      <Icon name={icon} className={cn('size-3.5', rotate && 'rotate-90')} />
    </button>
  )
}

/** A one-line field that looks like the heading it will print as. */
export function HeadingInput({
  label,
  value,
  placeholder,
  className,
  onChange,
}: {
  label: string
  value: string
  placeholder: string
  className?: string
  onChange: (value: string) => void
}) {
  return (
    <input
      aria-label={label}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'block w-full bg-transparent placeholder:font-normal placeholder:text-ink-300 focus:outline-none',
        className,
      )}
    />
  )
}

function BodyField({
  section,
  editing,
  placeholder,
  className,
  label,
  onChange,
  onRange,
}: {
  section: DraftSection
  editing: boolean
  placeholder: string
  className?: string | undefined
  label: string
  onChange: (value: string) => void
  onRange: (range: { start: number; end: number } | null) => void
}) {
  const [focusOnOpen, setFocusOnOpen] = useState(false)

  if (!editing) {
    return section.body.trim() ? (
      <div onClick={() => setFocusOnOpen(true)} className="cursor-text">
        <Prose body={section.body} className={className} />
      </div>
    ) : (
      <p
        onClick={() => setFocusOnOpen(true)}
        className={cn('cursor-text whitespace-pre-line text-ink-300', className)}
      >
        {placeholder.split('\n')[0]}
      </p>
    )
  }

  return (
    <AutoTextarea
      label={label}
      value={section.body}
      placeholder={placeholder}
      autoFocus={focusOnOpen}
      className={cn('leading-relaxed', className)}
      onChange={onChange}
      onRange={onRange}
    />
  )
}

/** A textarea that grows with its words, so the page never scrolls inside itself. */
export function AutoTextarea({
  label,
  value,
  placeholder,
  className,
  autoFocus,
  onChange,
  onRange,
  ...rest
}: {
  label: string
  value: string
  placeholder?: string
  className?: string
  autoFocus?: boolean
  onChange: (value: string) => void
  onRange?: (range: { start: number; end: number } | null) => void
} & Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'onSelect' | 'onKeyUp' | 'onMouseUp' | 'className' | 'rows' | 'placeholder' | 'autoFocus'
>) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }, [value])

  useLayoutEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  const report = () => {
    const node = ref.current
    if (!node || !onRange) return
    onRange(node.selectionEnd > node.selectionStart ? { start: node.selectionStart, end: node.selectionEnd } : null)
  }

  return (
    <textarea
      {...rest}
      ref={ref}
      aria-label={label}
      rows={1}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      onSelect={report}
      onKeyUp={report}
      onMouseUp={report}
      className={cn(
        'block w-full resize-none overflow-hidden bg-transparent placeholder:text-ink-300 focus:outline-none',
        className,
      )}
    />
  )
}

/** A photo on the page: dropped or chosen, stored as an attachment. */
export function PhotoField({
  section,
  onPatch,
  jobId,
  mode,
  variant,
  active,
}: {
  section: DraftSection
  onPatch: (next: Partial<DraftSection>) => void
  jobId: string | null
  mode: 'quote' | 'template'
  variant: 'cover' | 'figure' | 'panel'
  active: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [over, setOver] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const url = section.image ? fileUrl(section.image) : null

  const choose = async (file: File | undefined) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setProblem('That is not a photo.')
      return
    }
    setUploading(true)
    setProblem(null)
    try {
      const uploaded = await uploadFile(
        file,
        mode === 'template' || !jobId ? { scope: 'proposal', kind: 'PHOTO' } : { jobId, kind: 'PHOTO' },
      )
      onPatch({ attachmentId: uploaded.id, image: { id: uploaded.id, checksum: uploaded.checksum } })
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That photo did not upload.')
    } finally {
      setUploading(false)
      if (input.current) input.current.value = ''
    }
  }

  // An empty cover photo is optional, so it only asks while the cover is picked.
  if (!url && variant === 'cover' && !active && !uploading) return null

  return (
    <div
      className="group/photo relative"
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        if (!event.dataTransfer.files.length) return
        event.preventDefault()
        setOver(false)
        void choose(event.dataTransfer.files[0])
      }}
    >
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="sr-only"
        aria-label="Choose a photo"
        onChange={(event) => void choose(event.target.files?.[0])}
      />
      {url ? (
        <>
          <img
            src={url}
            alt=""
            className={cn(
              'w-full object-cover',
              variant === 'cover' ? 'aspect-[16/7]' : variant === 'figure' ? 'rounded-xl' : 'max-h-48 rounded-lg ring-1 ring-ink-200',
            )}
          />
          <div className="absolute top-3 left-3 flex gap-1.5 font-sans opacity-0 transition-opacity group-hover/photo:opacity-100 focus-within:opacity-100">
            <PhotoButton onClick={() => input.current?.click()}>{uploading ? 'Uploading…' : 'Replace'}</PhotoButton>
            <PhotoButton onClick={() => onPatch({ attachmentId: null, image: null })}>Remove</PhotoButton>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            input.current?.click()
          }}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-1.5 font-sans text-sm transition-colors',
            variant === 'cover' ? 'h-36 border-y border-dashed border-[var(--p-brand)]/40' : 'h-48 rounded-xl ring-2 ring-dashed',
            over ? 'bg-[var(--p-brand-soft)] text-[var(--p-brand-text)] ring-[var(--p-brand)]' : 'text-ink-500 ring-ink-200 hover:text-ink-800',
          )}
        >
          <Icon name="image" className="size-6" />
          {uploading ? 'Uploading…' : variant === 'cover' ? 'Add a cover photo (optional)' : 'Drop a photo here, or choose one'}
        </button>
      )}
      {problem ? (
        <p role="alert" className="mt-1 font-sans text-xs text-red-600">
          {problem}
        </p>
      ) : null}
    </div>
  )
}

/** What a photo block may hold: what the customer's page serves and the PDF can draw or name. */
const FILE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf'

/**
 * A photo block's photos and PDFs, edited where they sit: a grid of photos,
 * a row per PDF, and a drop zone that takes several at once. Decision 82.
 */
export function FilesField({
  section,
  onPatch,
  jobId,
  mode,
  className,
}: {
  section: DraftSection
  onPatch: (next: Partial<DraftSection>) => void
  jobId: string | null
  mode: 'quote' | 'template'
  className?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(0)
  const [over, setOver] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const photos = section.files.filter((file) => isProposalImage(file.mimeType))
  const documents = section.files.filter((file) => !isProposalImage(file.mimeType))

  const choose = async (list: FileList | null | undefined) => {
    const picked = [...(list ?? [])]
    if (picked.length === 0) return
    const usable = picked.filter((file) => isProposalImage(file.type) || file.type === 'application/pdf')
    const room = MAX_SECTION_FILES - section.files.length
    setProblem(
      usable.length < picked.length
        ? 'Only photos (JPEG, PNG, WebP) and PDFs can go on a proposal.'
        : usable.length > room
          ? `A block holds ${MAX_SECTION_FILES} files; the rest were left off.`
          : null,
    )

    const queue = usable.slice(0, Math.max(0, room))
    if (queue.length === 0) return
    setUploading(queue.length)
    const added: DraftFile[] = []
    try {
      for (const file of queue) {
        const kind = file.type === 'application/pdf' ? 'DOCUMENT' : 'PHOTO'
        const uploaded = await uploadFile(
          file,
          mode === 'template' || !jobId ? { scope: 'proposal', kind } : { jobId, kind },
        )
        added.push({ id: uploaded.id, checksum: uploaded.checksum, filename: uploaded.filename, mimeType: uploaded.mimeType })
        setUploading((count) => count - 1)
      }
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That file did not upload.')
    } finally {
      setUploading(0)
      if (added.length > 0) onPatch({ files: [...section.files, ...added] })
      if (input.current) input.current.value = ''
    }
  }

  const remove = (id: string) => onPatch({ files: section.files.filter((file) => file.id !== id) })
  const earlier = (id: string) => {
    const files = [...section.files]
    const at = files.findIndex((file) => file.id === id)
    // Earlier among its own kind: photos and PDFs are drawn apart.
    const kind = isProposalImage(files[at]!.mimeType)
    let before = at - 1
    while (before >= 0 && isProposalImage(files[before]!.mimeType) !== kind) before -= 1
    if (before < 0) return
    ;[files[before], files[at]] = [files[at]!, files[before]!]
    onPatch({ files })
  }

  return (
    <div
      className={cn('flex flex-col gap-3 font-sans', className)}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        if (!event.dataTransfer.files.length) return
        event.preventDefault()
        setOver(false)
        void choose(event.dataTransfer.files)
      }}
    >
      <input
        ref={input}
        type="file"
        multiple
        accept={FILE_ACCEPT}
        className="sr-only"
        aria-label="Choose photos or files"
        onChange={(event) => void choose(event.target.files)}
      />

      {photos.length > 0 ? (
        <ul aria-label="Photos" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {photos.map((file, index) => {
            const url = fileUrl(file)
            return (
              <li key={file.id} className="group/photo relative">
                {url ? (
                  <img src={url} alt={file.filename} className="aspect-[4/3] w-full rounded-xl object-cover ring-1 ring-ink-200" />
                ) : (
                  <span className="grid aspect-[4/3] w-full place-items-center rounded-xl bg-ink-50 text-xs text-ink-500">
                    {file.filename}
                  </span>
                )}
                <span className="absolute top-2 right-2 flex gap-1 opacity-0 transition-opacity group-hover/photo:opacity-100 focus-within:opacity-100">
                  {index > 0 ? (
                    <TileButton label={`Move ${file.filename} earlier`} icon="back" onClick={() => earlier(file.id)} />
                  ) : null}
                  <TileButton label={`Remove ${file.filename}`} icon="close" onClick={() => remove(file.id)} />
                </span>
              </li>
            )
          })}
        </ul>
      ) : null}

      {documents.length > 0 ? (
        <ul aria-label="Files" className="flex flex-col gap-2">
          {documents.map((file, index) => (
            <li key={file.id} className="group/file flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 ring-1 ring-ink-200">
              <FileBadge />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-800">{file.filename}</span>
              {index > 0 ? (
                <TileButton label={`Move ${file.filename} earlier`} icon="back" onClick={() => earlier(file.id)} />
              ) : null}
              <TileButton label={`Remove ${file.filename}`} icon="close" onClick={() => remove(file.id)} />
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          input.current?.click()
        }}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-xl text-sm ring-2 ring-dashed transition-colors',
          section.files.length === 0 ? 'h-36 flex-col' : 'h-12',
          over ? 'bg-[var(--p-brand-soft)] text-[var(--p-brand-text)] ring-[var(--p-brand)]' : 'text-ink-500 ring-ink-200 hover:text-ink-800',
        )}
      >
        <Icon name="image" className={section.files.length === 0 ? 'size-6' : 'size-4'} />
        {uploading > 0
          ? `Uploading ${uploading === 1 ? 'one file' : `${uploading} files`}…`
          : section.files.length === 0
            ? 'Drop photos or PDFs here, or choose them'
            : 'Add photos & files'}
      </button>
      {problem ? (
        <p role="alert" className="text-xs text-red-600">
          {problem}
        </p>
      ) : null}
    </div>
  )
}

function TileButton({ label, icon, onClick }: { label: string; icon: IconName; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className="grid size-7 place-items-center rounded-md bg-white/95 text-ink-600 shadow ring-1 ring-ink-200 hover:bg-white hover:text-ink-900"
    >
      <Icon name={icon} className="size-3.5" />
    </button>
  )
}

function PhotoButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className="rounded-md bg-white/95 px-2.5 py-1 text-xs font-medium text-ink-800 shadow ring-1 ring-ink-200 hover:bg-white"
    >
      {children}
    </button>
  )
}
